/**
 * Frontend entry point — routing, site chrome, and the consent gate.
 *
 * ── THE ROUTE TABLE ─────────────────────────────────────────────────────────
 *   /                      landing            public
 *   /login /register       auth               public, redirect when signed in
 *   /forgot-password       auth               public
 *   /reset-password?token= auth               public
 *   /verify-email?token=   auth               public
 *   /check-email           auth               signed in, unverified
 *   /legal /legal/:slug    content            public
 *   /faq                   content            public
 *   /news /news/:slug      content            public
 *   /account               REDIRECT -> /settings (bookmarks, support macros)
 *   /play                  BATTLE — the meta home, and every other meta screen
 *   /cards /shop /house    (`/events /profile /settings /crates /modes /social`)
 *                          claimed as a block by `isMetaPath`, signed in
 *   /arena                 the match, INSIDE the meta shell — header, left nav
 *                          rail at `md`, tab bar below it (`mountArenaInShell`)
 *                                             signed in, verified, consent current
 *   /crates/odds           published crate odds   PUBLIC — §4 regulatory hygiene
 *   /replay/:id            a shared replay        PUBLIC — a share link must work
 *   /admin /admin/*        operator console   claimed via `registerScreen`
 *   anything else          a real 404
 *
 * ── WHY `/play` IS NO LONGER THE MATCH ──────────────────────────────────────
 * It is the meta home screen — §6's Battle tab. The full-viewport match moved
 * to `/arena`, and `/play` kept its name because the landing page's call to
 * action, `engine/modes/invite-link.ts`'s share links (`INVITE_ROUTE`) and
 * every sibling module's `onClose: () => navigate('/play')` all point at it.
 * `/play/<invite code>` opens the modes hub with the code filled in.
 *
 * ── ONE ROUTER, NO ROUTER LIBRARY ───────────────────────────────────────────
 * This is a hand-rolled History-API switch and it stays one. The shell budget
 * is 150 KB gz and every byte of it is downloaded before a visitor sees
 * anything; the smallest credible router adds 4–10 KB gz to buy path
 * parameters, nested outlets and lazy route definitions, of which this
 * application uses none — there are two parameterised paths, both matched by a
 * regex below, and code splitting is already done by `await import()` in
 * `engine/bootstrap.ts` and `engine/admin/register.ts`. The registry in
 * `engine/layout/routes.ts` is the extension point for a screen that wants to
 * claim a path without this file importing it.
 *
 * ── WHAT IS SHELL AND WHAT IS NOT ───────────────────────────────────────────
 * Everything imported here statically is downloaded before first paint and
 * lives inside the 150 KB gz shell budget: the router, the auth screens, the
 * i18n runtime, the landing page, the content pages and the site chrome. The
 * match module, the training menu and the admin console are reached only
 * through `await import()` — see ADR-0005 and the guard in `vite.config.ts`.
 */
import { bootstrapSession, logout } from './core/auth/auth-api';
import { renderLogin } from './core/auth/login-screen';
// GAME-OWNED register screen (frontend/src/engine/auth), not core's. It adds the
// confirm-password field, the password policy and the consent record that core
// has no concept of. Every other auth screen below is still core's.
import { renderRegister } from './engine/auth';
import { renderForgotPassword } from './core/auth/forgot-password-screen';
import { renderResetPassword } from './core/auth/reset-password-screen';
import { renderVerifyEmail } from './core/auth/verify-email-screen';
import { renderCheckEmail } from './core/auth/check-email-screen';
import { authStore } from './core/state/auth-store';
import { connectSocket, disconnectSocket } from './core/network/socket';
import { fetchSiteInfo, isPreviewBuild, stripBrandMeta } from './core/preview/preview';
// The probe below needs the STATUS, and `ApiError` is the only place the client
// keeps it — `fetchSiteInfo` swallows its own failure by design (it has to
// return a shape), which is exactly why an outage was invisible to the shell.
import { ApiError, api } from './core/network/api';
import { mountGame, unmountGame } from './engine/bootstrap';
import { mountConsentGate } from './engine/consent';
import { adoptAccountLocale, initI18n } from './engine/i18n';
import { adoptAccountA11y, ensureLiveRegions, initA11y } from './engine/a11y';
/*
 * §9's CLIENT HALF, JOINED TO THE SHELL.
 *
 * `engine/analytics` shipped complete and with no callers at all, so
 * `funnel_step`, `fps_bucket` and `desync_detected` were never produced and the
 * four dashboards that read them were empty by construction. `shell.ts` is the
 * join; it loads the collector with `import()` after the first paint, so this
 * static import costs the SHELL budget only a route table and four functions.
 */
import {
  startShellAnalytics,
  stopShellAnalytics,
  trackRoute,
} from './engine/analytics/shell';
// §9's live-ops half. See `engine/config/config-store.ts`; the match's own
// ADR-0004 gate lives in `engine/bootstrap.ts`, which is where a join happens.
import { hydrateConfigFromCache, refreshFlagAnswers } from './engine/config';
import { renderLanding } from './engine/landing';
// SHELL-SAFE, and the ONLY file under `engine/meta/**` this entry may import:
// a frozen path table plus one lazy hop. Every meta SCREEN — the shell, the tab
// bar, the deck builder, the shop, the house, the crate board — is fetched with
// `await import()` behind `mountMetaRoute`, so none of it is charged to the
// 150 KB gz shell budget a cold visitor pays before first paint (ADR-0005).
import { ODDS_PATH, allowExit, guardPopState, isMetaPath, mountMetaRoute } from './engine/meta/route';
/*
 * §6's cold-start gate asks ONE question before routing, and `gate-state.ts`
 * has NO imports at all, so asking it costs the shell nothing. The GATE itself
 * — the coach and, through it, the whole match chunk — stays behind the
 * `await import()` in the `/play` branch below. A static import of
 * `onboarding-gate` here would put Pixi in the shell and `bundleLayerGuard`
 * would (correctly) fail the build.
 *
 * `resetOnboardingGate` is the other half: `signOut()` calls it, because the
 * next session on this device may be a different human with their own
 * onboarding row.
 */
import { onboardingSettled, resetOnboardingGate } from './engine/onboarding/gate-state';
// PUBLIC content pages. Deliberately NOT behind the session check: the terms
// and the privacy policy have to be readable by someone deciding whether to
// create an account, and by a crawler.
import {
  pageFromSearch,
  renderFaqPage,
  renderLegalIndex,
  renderLegalPage,
  renderNewsArticle,
  renderNewsList,
} from './engine/pages/content';
// The operator console claims /admin and /admin/* through the screen registry.
// Side-effect import, and deliberately a tiny one: the console itself is behind
// an `await import()` inside this module, so none of it is in the shell bundle.
import './engine/admin/register';
import './engine/shop/checkout-thanks';
// SHELL-SAFE, and the file `engine/layout/index.ts` deliberately does not
// re-export: `bootstrap.ts` imports it directly for the same reason. No pixi,
// no `engine/match/**` — two elements, a CSS custom property and the arithmetic
// that proves the contained stage cannot grow (ADR-0005, and its own docblock).
import { trackShellStageHeight } from './engine/layout/match-stage';
/*
 * THE ERROR PAGES, imported BY PATH rather than through `engine/layout`'s
 * barrel — the same rule `match-stage` above follows, and for the same reason:
 * the barrel re-exports the site chrome, the currency control and the footer,
 * and this file already pays for those, but naming the file keeps the import
 * graph honest about what a router needs. `error-screen.ts` itself imports only
 * `t`, `dom.ts` and `routes.ts`, all of which are shell already.
 */
import {
  errorKindForStatus,
  renderErrorScreen,
} from './engine/layout/error-screen';
import {
  ROUTES,
  adoptAccountCurrency,
  initCurrency,
  mountCookieConsent,
  mountLazy,
  mountSiteChrome,
  normalisePath,
  resolveScreen,
  type SiteChromeHandle,
} from './engine/layout';
/*
 * SOUND. `engine/audio` is the prefs store and the music bed — no `<audio>` is
 * constructed and no context is opened until a gesture, so importing it here
 * costs the shell budget a store and a class, and buys the whole app one mute
 * setting. `cues.ts` is imported BY PATH (the barrel deliberately does not
 * re-export it): it reaches the match mixer through an `await import()`, which
 * is what keeps ADR-0005's layer guard happy. See `mountShellAudio` below.
 */
import { audioManager } from './engine/audio';
import {
  installPressCueDelegate,
  installUiCueDelegate,
  playUiCue,
  primeUiCues,
} from './engine/audio/cues';

const app = document.getElementById('app')!;
let teardown: (() => void) | null = null;

/**
 * IS THE API THERE? — the one question the shell never asked.
 *
 * Every boot call swallowed its own failure so it could return a shape:
 * `fetchSiteInfo` falls back to a blank company, `bootstrapSession` answers
 * "no user", `refreshFlagAnswers` catches to `false`. Each of those is right on
 * its own and together they meant a DEAD BACKEND rendered as a working game —
 * the landing page, or a meta screen of zeroes, with every action failing
 * silently. Nothing anywhere said "the server is down".
 *
 * `null` = the API answered (or answered something that is not an outage: a
 * 401 or a 404 from `/site-info` is a deployment question, not a down server,
 * and must not put an outage page in front of a player who can still play).
 * Non-null = we could not get an answer, and `render()` below says so instead
 * of drawing a screen that cannot work.
 */
let apiOutage: { readonly status: number } | null = null;

/**
 * DID THE LAST RETRY FAIL? Reset on every render, set by the Retry handler when
 * the fresh probe came back with the same bad news. It is what turns the
 * re-render into an ANNOUNCEMENT rather than a silent, focus-losing repaint.
 */
let retryFailed = false;

/**
 * THE ROUTES AN OUTAGE MAY NOT SWALLOW.
 *
 * The outage page used to go in front of EVERY path, which took the legal
 * documents down with the game. That is the wrong trade twice over: those
 * pages are the ones a player wants precisely when the game is broken (the
 * privacy contact, the terms the consent gate sends them to read), and they
 * are the only routes here whose content is not the player's server-held
 * state, so an outage page over them is a locked door in front of a room that
 * is still lit.
 *
 * They are NOT wholly offline — `/legal` reads the CMS — but that page owns a
 * real error state of its own (`mountAsyncPage`), and a 5xx confined to one
 * service leaves it perfectly readable. An outage page cannot tell the
 * difference; the page itself can.
 *
 * Everything else genuinely needs the server: the landing page's call to
 * action opens registration, `/login` posts credentials, and every meta screen
 * reads a server-held balance. Those stay behind Retry.
 */
const OUTAGE_EXEMPT = /^\/legal(?:\/[a-z0-9-]{1,50})?$/;

/**
 * Ask the server whether it is there. `/site-info` because it is `@Public()`,
 * cheap, and already the first request this app makes, so an outage is detected
 * with the boot traffic rather than with a health endpoint invented for it.
 *
 * ── ONE REQUEST, NOT TWO, AND THE HALF OF IT THAT IS NOT FIXABLE HERE ───────
 * This used to run in `Promise.all` beside `fetchSiteInfo()`, which GETs the
 * same path — two identical requests on every boot, and on a dead server two
 * 15-second timeouts paid in parallel. It is now SEQUENTIAL and the probe goes
 * first, so an outage costs exactly one request and one timeout: the boot skips
 * `fetchSiteInfo()` entirely when the probe already knows the answer is
 * nothing.
 *
 * On the HEALTHY path there are still two, and honestly: `fetchSiteInfo` lives
 * in `core/preview/preview.ts`, which a game repo may not edit, and it exposes
 * no way to seed its private cache — while it also swallows the status this
 * probe exists to read, so it cannot be the probe either. Reusing the one
 * response across both would take a setter in core. Until then the extra GET is
 * a cached, `@Public()`, sub-kilobyte call on a warm server, and the case that
 * actually hurt a player — the dead one — is the case that now costs one.
 *
 * ONLY 5xx AND "no answer at all" COUNT. `status` 0 is axios's shape for a
 * request that never completed — DNS, a refused connection, the client's 15s
 * timeout — and is the commonest real outage on a phone, so it must not be
 * dropped for want of a number.
 */
async function probeApi(): Promise<void> {
  try {
    await api.get('/site-info');
    apiOutage = null;
  } catch (err) {
    const status = err instanceof ApiError ? err.status : 0;
    apiOutage = status === 0 || status >= 500 ? { status } : null;
  }
}

export function navigate(to: string) {
  /*
   * THE ONE PLACE EVERY EXIT PASSES THROUGH.
   *
   * A tab in the rail, a link in a screen, a programmatic redirect — they all
   * end up here, which is why the match-exit guard is asked HERE and not on any
   * one control. `allowExit` returns false when it has TAKEN OVER the
   * navigation (a dialog is up); it calls `resume` itself on a yes, so the
   * navigation is deferred, never dropped. With no live match the guard is null
   * and this is one comparison.
   *
   * This call and the `popstate` wrapper below are the whole of the guard's
   * reachability. Without them `meta/route.ts` can arm, disarm and confirm
   * perfectly and a player still walks out of a live match mid-fight: the
   * module was written, tested and reachable from nothing.
   */
  if (!allowExit(to, navigate)) return;
  // A NEW page starts at its top. Without this, clicking a footer link (Terms,
  // Privacy, Cookie Policy…) from far down a long page pushed the new route but
  // left the document scrolled where the footer was, so the reader landed in
  // the middle — or, on a short legal page, clamped somewhere near the bottom
  // and it read as "it scrolled me". Only on a real push: back/forward come
  // through `popstate`, where the browser restores its own scroll position, and
  // a same-path re-render (an in-app state repaint) must keep the reader where
  // they are.
  const pushed = location.pathname !== to;
  if (pushed) history.pushState(null, '', to);
  render();
  if (pushed) window.scrollTo(0, 0);
}

/**
 * End the session from anywhere in the site chrome.
 *
 * Three things, and only the first is core's: revoke the refresh family, drop
 * the match websocket, and leave whatever page needed a session. Doing them in
 * that order means the socket is never left holding a seat for an account that
 * has just been signed out.
 */
function signOut(): void {
  void logout();
  disconnectSocket();
  // The onboarding memo is a cache of one server answer for one session; the
  // next person to sign in on this device has their own onboarding row, and a
  // stale `true` would walk a brand-new account straight past §6 into an empty
  // Battle screen.
  resetOnboardingGate();
  // Discard, never flush: a sign-out must not be followed by one last upload of
  // the session that preceded it. The next sign-in mints a fresh session id.
  stopShellAnalytics();
  navigate(ROUTES.home);
}

/**
 * THE MATCH, MOUNTED INSIDE THE META SHELL — header on top, nav rail on the
 * left at `md`, tab bar at the bottom below it.
 *
 * ── WHY THIS EXISTS ─────────────────────────────────────────────────────────
 * `/arena` used to be `mountGame(app, …)` on its own route, OUTSIDE the shell.
 * `hasSiteChrome(ROUTES.arena)` is false (`layout/routes.ts`) and the shell was
 * only ever reached down the `isMetaPath` branch, so the rail's LOG OUT was
 * unreachable from the match and the training menu's own sign-out
 * (`meta/menu.ts:404-413`) was the single rendered way out — which is why
 * `test/meta-patterns.test.ts` forbids deleting it. Putting the shell around
 * the surface is what removes that dependency, on every device: at `md` the
 * rail's exit is on screen beside the canvas, and below `md` the tab bar is,
 * two taps from the sign-out in `/settings`.
 *
 * ── THE SEAM, AND WHY IT IS THIS ONE ────────────────────────────────────────
 * `MetaRouteOptions.mount` is a `ScreenMounter` — the shell's one injection
 * point for "what goes in the content column". Injecting the arena DOWNWARD
 * through it is what lets the match live inside the shell while nothing under
 * `engine/meta/**` ever names `engine/match/**`: `meta-screens.test.ts` reads
 * those seven files as text and asserts exactly that (ADR-0005), and a dynamic
 * `import('@/engine/match')` inside `screens.ts` would have slipped past the
 * regex while defeating its intent. It is the same argument, and a strictly
 * smaller version of the same shape, as IA-AND-NAVIGATION §3.5's `ArenaMounter`
 * — when that lands in `route.ts`/`shell.ts`, this collapses into it.
 *
 * ── THE PATH IS `/play`, THE URL IS STILL `/arena` ──────────────────────────
 * The shell is mounted for `ROUTES.play`, so the Battle tab reads as current —
 * the arena is the thing Battle opens, and marking no tab current would leave a
 * five-item nav with nothing selected. The URL is NOT rewritten: `/arena` stays
 * a real, reloadable, deep-linkable address that opens a match, which is the
 * only reason the Battle screen's existing play button still works. Until the
 * `ArenaMounter` seam lands there is no other way in, so turning `/arena` into
 * a redirect today would make the arena unreachable rather than relocated.
 *
 * ── THE STAGE ───────────────────────────────────────────────────────────────
 * `trackShellStageHeight` publishes `--rga-stage-h` on the host the shell hands
 * us, and `createMatchStage` reads it. That is the whole of the containment:
 * a definite `calc(100dvh - <measured chrome>px)`, never a percentage. Read
 * `engine/layout/match-stage.ts`'s docblock before touching either half — the
 * percentage version is a documented page that never stops growing.
 */
/* ═══════════════════════════════════════════════════════════════ sound ══ */

/**
 * THE SHELL'S AUDIO BED AND ITS UI CUES — the mount that had never been written.
 *
 * `engine/audio/manager.ts` shipped complete, documented down to the autoplay
 * lock, and was called by NOTHING: `audioManager()` had zero callers in `src`,
 * so `setScene` was never reached and the bed never started. The same was true
 * of the UI cue vocabulary. This function is the join, and it is deliberately
 * three lines of intent:
 *
 *   1. THE UNLOCK GESTURE, on `window`, once. Every browser refuses audio that
 *      starts outside a user gesture; the manager records the scene it wants
 *      and plays nothing until `unlock()` runs from inside a real one.
 *      `attachUnlockGestures` wires that and detaches itself on the first
 *      success — so the bed starts on the visitor's first click ANYWHERE, not
 *      on a control we remembered to instrument.
 *   2. THE SAME GESTURE BUILDS THE CUE MIXER. `primeUiCues()` is the
 *      `await import()` of the match mixer, started from the gesture so the
 *      AudioContext it creates is allowed to run (see `cues.ts`'s header for
 *      the sticky-vs-transient activation detail). It is NOT started at boot:
 *      a visitor who never clicks never downloads it.
 *   3. THE TWO DELEGATED LISTENERS. One `pointerover` and one `pointerup` for
 *      the entire application — the shell rebuilds its DOM on every navigation,
 *      so a listener per control would be a leak per navigation.
 *
 * Nothing here can throw into the bootstrap: every call below is total.
 */
function mountShellAudio(): void {
  const manager = audioManager();
  manager.attachUnlockGestures(window);

  // The gesture that unlocks the bed is also the gesture that may build the
  // mixer. `once` — after the first one the mixer either exists or has been
  // marked unavailable, and `primeUiCues` is idempotent anyway.
  const prime = (): void => {
    void primeUiCues();
  };
  for (const event of ['pointerdown', 'touchend', 'keydown'] as const) {
    window.addEventListener(event, prime, { passive: true, once: true });
  }

  installUiCueDelegate(document);
  installPressCueDelegate(document);
  applyAudioScene(normalisePath(location.pathname));
}

/** The route the bed is currently playing for. `null` before the first call. */
let audioScenePath: string | null = null;

/**
 * Point the music bed at the right scene for a route, and fire the
 * entering-the-arena cue on the way in.
 *
 * `/arena` gets `'none'`, NOT `'battle'`: the match runs its own adaptive
 * three-stem score (`engine/match/audio/music.ts`), and `manager.ts`'s own
 * header is explicit that the caller decides which of the two is in charge
 * because two music systems playing at once is mush. Everything else — the
 * landing page, the auth screens, all eleven meta screens — shares one menu
 * bed, which is why this is a route rule and not eleven screen rules.
 *
 * Idempotent: `setScene` ignores a repeat of the current scene rather than
 * restarting the loop, so calling this on every `render()` is free.
 */
function applyAudioScene(path: string): void {
  const wasArena = audioScenePath === ROUTES.arena;
  const isArena = path === ROUTES.arena;
  audioScenePath = path;
  audioManager().setScene(isArena ? 'none' : 'menu');
  /*
   * Only on the TRANSITION in — a re-render of `/arena` is not a second
   * entrance.
   *
   * `whenReady` because of a race that would otherwise make this cue silent on
   * the FIRST match of every session: the tap on Battle is both the navigation
   * and, very often, the visitor's first gesture, so the mixer is still one
   * `await import()` away from existing when the route changes. See `cues.ts`.
   */
  if (isArena && !wasArena) playUiCue('enter_arena', { whenReady: true });
}

function mountArenaInShell(user: Parameters<typeof mountGame>[1]): () => void {
  return mountMetaRoute({
    root: app,
    path: ROUTES.play,
    search: '',
    navigate,
    onSignOut: signOut,
    mount: (ctx) => {
      // BEFORE `mountGame`: the property has to be on the host before the stage
      // is built, or the first frame the renderer measures is a `100dvh` box
      // inside a shorter column and the scene resizes visibly afterwards.
      const releaseHeight = trackShellStageHeight(ctx.host);
      // 'container': the stage sizes to the shell's content column, not the
      // viewport, and picks up CONTAINED_STAGE_CLASS.
      const stopGame = mountGame(ctx.host, user, () => { signOut(); }, 'container');
      return Promise.resolve(() => {
        stopGame();
        releaseHeight();
      });
    },
  });
}

/**
 * The header and the footer, as siblings of `#app`.
 *
 * They live OUTSIDE `#app` because every screen owns `#app` and replaces its
 * contents wholesale — the core auth screens assign `root.innerHTML`, and the
 * match hands the element to Pixi. Which routes get them is one predicate,
 * `hasSiteChrome`, and not a `Set` maintained here; see `engine/layout/chrome.ts`.
 *
 * MOUNTED AFTER `initI18n()`, NOT AT MODULE SCOPE. Every string in the header
 * and the footer comes from `t()`, which returns the KEY until a catalogue is
 * loaded — so mounting it here would paint `nav.home` and `footer.tagline`
 * across the top and bottom of the first frame and then swap them. It is
 * nullable for the window before that, and `render()` copes: a click that
 * arrives before bootstrap finishes routes without chrome, and the bootstrap's
 * own `render()` follows immediately.
 */
let chrome: SiteChromeHandle | null = null;

/**
 * A DELETION IS ON SCREEN. Set by the `onSessionEnded` passed to the meta shell
 * (the account section lives inside `/settings` now), read by the auth-store
 * subscriber at the bottom of this file, which otherwise ejects a session-less
 * player off every meta path to `/login` — on top of the deletion farewell.
 *
 * Cleared when a user appears again, so a later sign-in in the same page load
 * gets the normal eject back.
 */
let accountEnded = false;

function render() {
  teardown?.();
  teardown = null;

  const path = normalisePath(location.pathname);

  // The music bed follows the route, and it does so BEFORE the branches below:
  // the outage screen and a 404 are still the menu, and an early `return` must
  // not leave the battle scene playing behind them.
  applyAudioScene(path);

  /*
   * THE SERVER IS NOT THERE — say so, on every route that needs it.
   *
   * FIRST, above every other branch: with the API unreachable there is no
   * screen in this application that can do its job. The landing page's call to
   * action opens registration (a POST that will fail), `/login` posts
   * credentials, and all eleven meta screens read server-held balances.
   * Drawing any of them would be the zombie app this flag exists to end.
   *
   * EXCEPT the routes in `OUTAGE_EXEMPT`, which fall through to render
   * normally — see the comment on it for why the legal documents must survive
   * an outage that the rest of the app does not.
   *
   * The way out is RETRY and only retry — `renderErrorScreen` draws no home
   * link here on purpose, because home is this same page until the server
   * answers. A retry that comes back with the same answer re-renders with
   * `retryFailed`, which is what gives it a spoken result and a focus target
   * instead of a silent repaint onto `<body>`. `probeApi()` then re-renders: on success `apiOutage` is null and
   * the route the player originally asked for is what paints, so a recovered
   * server drops them exactly where they were going.
   */
  if (apiOutage !== null && !OUTAGE_EXEMPT.test(path)) {
    const { status } = apiOutage;
    const failed = retryFailed;
    retryFailed = false;
    teardown = renderErrorScreen(app, {
      kind: errorKindForStatus(status),
      status,
      retryFailed: failed,
      onRetry: () => {
        void probeApi().then(() => { retryFailed = apiOutage !== null; render(); });
      },
    });
    return;
  }
  retryFailed = false;

  /*
   * `/account` MOVED INSIDE THE GAME (IA-AND-NAVIGATION §4).
   *
   * Every block it held — password, consent, the Art. 15 export, erasure — is a
   * SECTION of the `/settings` meta screen now (`meta/settings-screen.ts`'s
   * account host), under the shell's own header and rail. Rendering the old
   * page as well would put the landing header and footer back around it, which
   * is the exact complaint this answers, and would give the player two copies
   * of the same delete-account form on two URLs.
   *
   * A REDIRECT, NOT A 404: the path is in bookmarks and in support macros.
   * `replaceState` rather than push, so Back goes where the player came from
   * instead of bouncing them through the redirect again — and BEFORE
   * `chrome.update(path)` below, or the site chrome paints for one frame on the
   * way past.
   */
  if (path === ROUTES.account) {
    history.replaceState(null, '', ROUTES.settings);
    render();
    return;
  }

  const params = new URLSearchParams(location.search);
  const user = authStore.get().user;

  chrome?.update(path);
  // §9. Deduplicated on the path inside, because `render()` also runs when the
  // identity guess is corrected or a preference is adopted — neither is a view.
  trackRoute(path);

  const afterAuth = () => {
    const u = authStore.get().user;
    if (u !== null && !u.emailVerified) { navigate('/check-email'); return; }
    navigate(ROUTES.play);
  };

  switch (path) {
    case ROUTES.home:
      // PUBLIC, and public for signed-in players too. The landing page is the
      // marketing front door; a returning player gets "back to the arena" as
      // its primary action rather than being bounced past it.
      teardown = renderLanding(app);
      break;
    /*
     * ── THE TWO DOORS IN, AND THERE ARE NO OTHERS ──────────────────────────
     *
     * Registration and sign-in are the only ways to hold a playable identity:
     * there is no anonymous one to earn a collection any more. A session
     * already in hand is bounced off both, because it is already where they
     * would take it.
     */
    case ROUTES.login:
      if (user) {
        navigate(user.emailVerified ? ROUTES.play : '/check-email');
        return;
      }
      teardown = renderLogin(app, afterAuth);
      break;
    case ROUTES.register:
      if (user) {
        navigate(user.emailVerified ? ROUTES.play : '/check-email');
        return;
      }
      teardown = renderRegister(app, afterAuth);
      break;
    case '/forgot-password':
      if (user) { navigate(ROUTES.play); return; }
      teardown = renderForgotPassword(app);
      break;
    case '/reset-password':
      teardown = renderResetPassword(app, params.get('token') ?? '', () => navigate(ROUTES.login));
      break;
    case '/verify-email':
      teardown = renderVerifyEmail(app, params.get('token') ?? '');
      break;
    case '/check-email':
      if (!user) { navigate(ROUTES.login); return; }
      teardown = renderCheckEmail(app, user.email, () => { disconnectSocket(); navigate(ROUTES.login); });
      break;
    case ROUTES.arena: {
      // No session, no match: registration or sign-in is the only way in.
      if (!user) { navigate(ROUTES.register); return; }
      if (!user.emailVerified) { navigate('/check-email'); return; }
      /*
       * THE CONSENT GATE, and the reason the socket is connected inside it
       * rather than beside it.
       *
       * A player whose acceptance was superseded by a new publish must be shown
       * the new document and asked again — the match socket refuses their
       * upgrade with a 403 either way (`game/match/auth.ts` step 8), so
       * connecting first would produce an unexplained disconnection instead of
       * a page that says what happened and offers the button that fixes it.
       */
      teardown = mountConsentGate(app, {
        onSatisfied: () => {
          connectSocket();
          return mountArenaInShell(user);
        },
        onDecline: () => navigate(ROUTES.home),
      });
      break;
    }
    case ROUTES.legal:
      teardown = renderLegalIndex(app);
      break;
    case ROUTES.faq:
      teardown = renderFaqPage(app);
      break;
    case ROUTES.guide:
      /*
       * THE SMALL GUIDE, and it is LAZY on purpose.
       *
       * `renderLanding` is imported statically above, so anything re-exported
       * from `engine/landing` lands in the shell chunk every cold visitor
       * downloads. The guide is a page you reach by clicking a nav link, which
       * makes it exactly the shape `mountLazy` exists for — the same trade the
       * published crate odds make below.
       */
      teardown = mountLazy(app, async () => {
        const { renderGuidePage } = await import('./engine/landing/guide-page');
        return (root) => renderGuidePage(root);
      });
      break;
    case ROUTES.news:
      teardown = renderNewsList(app, pageFromSearch(location.search));
      break;
    case ODDS_PATH:
      /*
       * THE PUBLISHED CRATE DROP TABLES — §4, and deliberately PUBLIC.
       *
       * Not behind the session check and not a meta screen: a player deciding
       * whether to spend, and anyone auditing the claim that the published
       * odds are the odds the server rolls, has to be able to read this page
       * without an account. It is a document, so it keeps the site chrome
       * (`hasSiteChrome` does not claim it — see `layout/routes.ts`).
       */
      teardown = mountLazy(app, async () => {
        const { renderDropTablesScreen } = await import('./engine/crates');
        return (root) => renderDropTablesScreen(root, { onClose: () => history.back() });
      });
      break;
    default: {
      /*
       * THE META SCREENS, claimed as a block.
       *
       * `isMetaPath` is the whole test — one frozen table in
       * `engine/meta/route.ts` plus the `/play/<invite code>` pattern — so
       * adding the eleventh meta screen is a line there and nothing here. A
       * path it does not claim falls straight through to the 404 below, which
       * is why an unknown URL never lands on a blank meta shell.
       *
       * NOT behind the consent gate, which guards `/arena`. The gate costs an
       * HTTP round trip on every mount, and the shell is rebuilt on every tab
       * tap — one request per tap to re-ask a question whose only consequence
       * is refusing a match. The match is where it is asked, and where the
       * server refuses anyway (`game/match/auth.ts` step 8).
       */
      if (isMetaPath(path)) {
        /*
         * ── A META SCREEN NEEDS A REAL ACCOUNT ─────────────────────────────
         *
         * Every one of them reads or writes server-held value, and the server
         * has exactly one kind of caller: a registered player. No session goes
         * to `/register`; an unverified one to the verification gate.
         */
        if (!user) { navigate(ROUTES.register); return; }
        if (!user.emailVerified) { navigate('/check-email'); return; }
        /*
         * ── §6's COLD START, IN FRONT OF THE BATTLE HOME ───────────────────
         *
         * Onboarding is not a place you navigate to; it is a thing that
         * happens once, in front of wherever the player was going. So it is a
         * GATE here rather than a route: giving it a URL would make it
         * bookmarkable, back-buttonable and skippable by typing.
         *
         * `/play` ONLY. The tutorial belongs in front of the Battle home a
         * cold visitor lands on; gating `/shop` on it would hand a
         * deep-linked player a tutorial instead of the screen they asked for.
         *
         * `onboardingSettled()` is a per-page-load memo, so the meta shell
         * being rebuilt on every tab tap does not become one `GET /onboarding`
         * per tap. It is reset by `resetOnboardingGate()` on sign-out, because
         * the next session may be a different human.
         *
         * `onSettled` re-renders rather than navigating: the path has not
         * changed, and the screen the player was going to is the one that
         * should now mount. The gate guarantees exactly one call, on every
         * failure path included — a tutorial that cannot load must never
         * become a locked door in front of the game.
         */
        if (path === ROUTES.play && !onboardingSettled()) {
          teardown = mountLazy(app, async () => {
            const { mountOnboardingGate } = await import(
              './engine/onboarding/onboarding-gate'
            );
            return (root) => mountOnboardingGate({ root, onSettled: () => render() });
          });
          break;
        }
        teardown = mountMetaRoute({
          root: app,
          path,
          search: location.search,
          navigate,
          onSignOut: signOut,
          /*
           * THE ACCOUNT ENDED — a password change revoked every session, or the
           * account was deleted. Not the same callback as `onSignOut`, and the
           * difference is the whole reason it is threaded: a password change and
           * a deletion both revoke server-side, so the local session has to end
           * too or the SPA sits on a dead cookie showing live-looking numbers
           * until the next 401.
           *
           * IT MUST NOT NAVIGATE. The account screen renders a farewell after a
           * deletion — with its own link back to sign-in — and that farewell is
           * the only confirmation the player ever gets that the account is gone.
           * `accountEnded` is what keeps the auth-store subscriber below from
           * ejecting them off `/settings` and destroying it; `/account` used to
           * be exempt from that eject by being a non-meta path, and this flag is
           * what replaces that exemption now that the section lives inside the
           * shell.
           */
          onSessionEnded: () => {
            accountEnded = true;
            disconnectSocket();
            authStore.set({ user: null, status: 'idle', error: null });
          },
        });
        break;
      }

      /*
       * A SHARED REPLAY LINK. PUBLIC, and it has to stay that way: the server
       * mints `<APP_URL>/replay/<id>` (`ReplayService.shareUrl`) and the
       * endpoint behind it is `@Public()` precisely so the link works for
       * whoever it was sent to. A route guard here would undo that decision in
       * the client and turn every shared replay into a login wall.
       *
       * The id's alphabet is NOT validated — the server owns what a replay id
       * looks like, and a client that rejects a format the server later widens
       * breaks every old link. An unknown id and a malformed one get the same
       * answer from the screen anyway.
       */
      const replay = /^\/replay\/([^/?#]+)$/.exec(path);
      if (replay !== null) {
        const replayId = decodeURIComponent(replay[1] as string);
        teardown = mountLazy(app, async () => {
          const { renderSharedReplayScreen } = await import('./engine/social');
          return (root) =>
            renderSharedReplayScreen(root, replayId, { onClose: () => navigate(ROUTES.home) });
        });
        break;
      }

      // The two parameterised content routes.
      const legal = /^\/legal\/([a-z0-9-]{1,50})$/.exec(path);
      if (legal !== null) { teardown = renderLegalPage(app, legal[1] as string); break; }
      const post = /^\/news\/([a-z0-9-]{1,120})$/.exec(path);
      if (post !== null) { teardown = renderNewsArticle(app, post[1] as string); break; }

      // Anything else: a screen that claimed the path via `registerScreen`,
      // otherwise a real 404 page — NOT a redirect to /login, which used to
      // tell a visitor with a stale bookmark that they had been signed out.
      const screen = resolveScreen(path);
      // `renderErrorScreen({kind:'not_found'})` IS `renderNotFound`, extended:
      // it calls it and adds the line saying a mistyped address is nobody's
      // failure. The heading, the sentence and the link home are unchanged, so
      // the three existing tests that assert them still hold.
      teardown =
        (screen ? screen(app, path) : renderErrorScreen(app, { kind: 'not_found' })) ?? null;
    }
  }
}

// The back button never asks the router first — by the time this fires the URL
// has already moved — so the guard restores it and asks. See `guardPopState`.
window.addEventListener('popstate', guardPopState(render));

document.addEventListener('click', (e) => {
  const mouse = e as MouseEvent;
  // Let the browser do what the user asked: new tab, new window, download.
  if (mouse.button !== 0 || mouse.metaKey || mouse.ctrlKey || mouse.shiftKey || mouse.altKey) {
    return;
  }
  if (e.defaultPrevented) return;

  const target = e.target instanceof Element ? e.target : null;
  const a = target?.closest('a[href]') as HTMLAnchorElement | null;
  if (!a || a.target || a.hasAttribute('download') || a.origin !== location.origin) return;

  // In-page fragments — the skip link, the landing page's "how it works"
  // anchor — are the browser's job. Routing them re-renders the screen and
  // throws away both the scroll position and the focus move, which is exactly
  // what a skip link exists to perform.
  const href = a.getAttribute('href') ?? '';
  if (href.startsWith('#')) return;
  if (a.hash !== '' && a.pathname === location.pathname && a.search === location.search) return;

  e.preventDefault();
  navigate(a.pathname + a.search);
});

(async () => {
  // The probe first, site-info second and only if there is a server to ask.
  // site-info drives preview-mode gating throughout the app; with the API down
  // it can only return its blank fallback, so asking for it would buy a second
  // 15-second timeout in front of a page that is going to say "we are down".
  await probeApi();
  if (apiOutage === null) await fetchSiteInfo();
  if (isPreviewBuild()) stripBrandMeta();

  // The language has to be in force before the first render or every screen
  // paints raw translation keys and then flips. `initI18n` never rejects, so
  // the pair can be awaited together without a failed locale chunk taking the
  // session bootstrap down with it.
  /*
   * §6 ACCESSIBILITY, APPLIED BEFORE THE FIRST PAINT.
   *
   * `initA11y()` is synchronous — it reads this device's stored answers and
   * writes them onto `<html>`. It runs BEFORE `render()` because a text scale
   * applied afterwards is a reflow the player watches happen, and because a
   * player who asked for reduced motion would otherwise see exactly one
   * animation: the one they asked not to see.
   *
   * `ensureLiveRegions()` creates the two `aria-live` nodes NOW rather than at
   * the first announcement. A live region must be in the document before its
   * text changes or the change is not announced at all — creating the node and
   * filling it in the same frame is the single most common way an `aria-live`
   * region ships silently broken.
   */
  initA11y();
  ensureLiveRegions(document);
  /*
   * §9's LIVE-OPS STATE, FROM THIS DEVICE'S CACHE, SYNCHRONOUSLY.
   *
   * `hydrateConfigFromCache` re-validates the stored document with the same
   * function the network path uses, so a corrupted or hand-edited entry is
   * discarded rather than trusted because it is local. It is synchronous and
   * localStorage-only, so it costs the first frame nothing and means a
   * returning player is already on the current card table and the current
   * feature flags instead of being one round trip behind.
   */
  hydrateConfigFromCache();

  await Promise.all([initI18n(), bootstrapSession()]);
  chrome = mountSiteChrome({ app, onSignOut: signOut });
  /*
   * SOUND, BEFORE THE FIRST RENDER AND AFTER `initA11y()`.
   *
   * Before `render()` so the very first `applyAudioScene` is this one and the
   * router's is the idempotent repeat, and after `initA11y()` because the
   * hover cue asks `motionReduced()`, which reads the answer `initA11y` has
   * just written onto `<html>`. Nothing audible happens here: every path is
   * still behind the browser's autoplay lock until the visitor's first gesture.
   */
  mountShellAudio();
  render();

  // The cookie-consent notice, once per visitor, on every page. Appended to the
  // body rather than the chrome (which wraps only document routes) so the game
  // shows it too; a no-op once a choice is stored. See `cookie-consent.ts`.
  mountCookieConsent(document.body);
  /*
   * §9, AFTER THE FIRST PAINT AND NEVER BEFORE IT.
   *
   * Not awaited and deliberately placed below `render()`: the collector is a
   * dynamic import followed by a consent round trip, and neither belongs inside
   * the 90-second cold-start budget §6 is judged on. It re-fires the landing
   * step itself once consent is known, because `funnel()` drops everything
   * until then — see `engine/analytics/shell.ts`.
   */
  startShellAnalytics(normalisePath(location.pathname));

  // Account preferences, once the session is known. Both READ without writing
  // back: a stale value on this device must never overwrite a fresh choice
  // made on another one. `initCurrency` resolves account > device > server
  // default by itself, so there is no separate adopt call at boot.
  void adoptAccountLocale();
  void initCurrency();
  // Same rule, same reason: read without writing back. See `engine/a11y/store.ts`
  // for why this is a no-op against today's core preferences endpoint.
  void adoptAccountA11y();
  /*
   * The FLAG ANSWERS, which are per-account: a percentage rollout buckets on
   * the caller's id, so the same flag is a different answer for two players and
   * the server is the only thing that may decide which. Not awaited — every
   * `isFeatureEnabled` read falls back to the flag's default until it lands.
   */
  void refreshFlagAnswers().catch(() => false);

  // A sign-in later in the session is the other moment the account's stored
  // preferences become knowable, and it is not a page load — without this, a
  // player who set Latvian on their laptop signs in on a phone and stays in
  // whatever the phone's browser advertised.
  let lastUserId: string | null = authStore.get().user?.id ?? null;
  authStore.subscribe((s) => {
    const userId = s.user?.id ?? null;
    // Captured BEFORE `lastUserId` is updated below: the eject at the bottom of
    // this callback is about a session that ENDED, and that is a transition,
    // not a state. See the comment there.
    const hadUser = lastUserId !== null;
    if (userId !== lastUserId) {
      lastUserId = userId;
      if (userId !== null) {
        accountEnded = false;
        void adoptAccountLocale();
        void adoptAccountCurrency();
        void adoptAccountA11y();
      }
    }
    // A session that ends mid-visit must not leave a player inside the match —
    // or inside the meta shell, which is now the bigger surface: eleven screens
    // that all read server-held balances and would sit there showing the signed
    // -out player the previous account's Gold until the next 401.
    // A DELETION IS THE EXCEPTION, and it is now inside the shell: the account
    // section of `/settings` renders the farewell that is the only confirmation
    // the account is gone, so `accountEnded` suppresses the eject rather than a
    // path exemption. See the `onSessionEnded` passed to `mountMetaRoute`.
    if (!s.user) {
      /*
       * A SESSION THAT NEVER EXISTED IS NOT A SESSION THAT ENDED.
       *
       * `bootstrapSession`'s refresh answers 401 on every cold ANONYMOUS load
       * and pushes `{user: null}` through this store. Reacting to that state
       * would bounce a first-time visitor who is standing on a public page to
       * `/login` before they have done anything at all, and would fight the
       * router, which already sends an account-less caller to `/register`.
       *
       * So gate on the TRANSITION: bounce only when a user we had is gone.
       */
      if (!hadUser) return;
      if (accountEnded) return;
      const here = normalisePath(location.pathname);
      if (here === ROUTES.arena || isMetaPath(here)) navigate(ROUTES.login);
    }
  });
})();

window.addEventListener('beforeunload', () => {
  teardown?.();
  unmountGame();
});
